import React, { FC, useEffect, useState, useCallback } from 'react';
import { Bmis } from '@packages/models/api';
import { BmisBatteryBadge } from '@web/bmis/components/bmis-battery-badge';
import { BmisLayout } from '@web/bmis/components/bmis-layout';
import { bmisService } from '@web/services/singletons';
import { createUseStyles } from 'react-jss';
import { DATE_FORMAT } from '@web/config/constants';
import { formatToTitleCase, getDisplayDate, getDisplayValue } from '@web/utils';
import { Icon, Typography } from '@packages/ui/shared';
import { Link, useParams } from 'react-router-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { setVin, useBmisDispatch, useBmisState } from '@packages/contexts/bmis';
import { TableCell, TableHeader, TableRenderer, TableRow } from '@web/components/table';
import { useTranslation } from 'react-i18next';
import colors from '@packages/core/styles/colors';

interface BmisTableRowModel extends Bmis {
    checked: boolean;
    rowId: string;
}

const BMIS_TITLE = 'BMIS - Battery tests';
const EMPTY_STATE_DESC = 'No BMIS test available for this vehicle yet.';
const BMIS_TOOLTIP =
    'Battery data updates daily but may not be the latest test result. Check Midtronics for real-time info.';

const useBmisListStyles = createUseStyles({
    tableContainer: {
        minHeight: '82vh',
    },
    outerTable: {
        height: '100%',
        minHeight: 'auto',
    },
    testDecision: {
        minWidth: '225px',
    },
    batteryService: {
        minWidth: '170px',
    },
    locationId: {
        minWidth: '120px',
    },
    tableCell: {
        minWidth: '80px',
    },
    link: {
        fontSize: 15,
        fontWeight: 400,
        '&:hover': {
            textDecoration: 'underline!important',
        },
    },
    tooltip: {
        marginTop: 5,
        width: 272,
        '& .tooltip-inner': {
            backgroundColor: colors.graySeven,
            borderRadius: 4,
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '20px',
            maxWidth: 272,
            padding: '3px 8px',
            textAlign: 'left',
            width: 272,
        },
    },
});

const BmisTableHeader: FC = () => {
    const { t } = useTranslation();
    const classes = useBmisListStyles();

    const TEST_TABLE_HEADER_TITLES = [
        { title: 'Latest Battery Test Decision', width: '18%', className: classes.testDecision },
        { title: 'Battery Service Date', width: '17%', className: classes.batteryService },
        { title: 'Test Type', width: '25%', className: classes.tableCell },
        { title: 'Voltage', width: '10%', className: classes.tableCell },
        { title: 'CCA', width: '10%', className: classes.tableCell },
        { title: 'Tool Type', width: '10%', className: classes.tableCell },
        { title: 'Location ID ', width: '10%', className: classes.locationId },
    ];

    return (
        <TableRow>
            {TEST_TABLE_HEADER_TITLES.map((header) => (
                <TableHeader key={header.title} width={header.width} className={header.className}>
                    <Typography variant="label">{t(header.title)}</Typography>
                </TableHeader>
            ))}
        </TableRow>
    );
};

const BmisTableRow: FC<{ rowData: BmisTableRowModel }> = ({ rowData }) => {
    const classes = useBmisListStyles();
    const { rowId, finalBatteryTestDecision, serviceDateandTime, testType, voltage, toolType, cca, locationId } =
        rowData;

    return (
        <TableRow key={rowId}>
            <TableCell>
                <Typography numberOfLines={1}>
                    <BmisBatteryBadge label={getDisplayValue(finalBatteryTestDecision)} />
                </Typography>
            </TableCell>
            <TableCell>
                <Typography numberOfLines={1} variant="label">
                    <Link className={classes.link} to={`./${rowId}`}>
                        {getDisplayDate(serviceDateandTime, DATE_FORMAT.MON_DAY_YEAR)}
                    </Link>
                </Typography>
            </TableCell>
            <TableCell>
                <Typography numberOfLines={1}>{getDisplayValue(formatToTitleCase(testType))}</Typography>
            </TableCell>
            <TableCell>
                <Typography numberOfLines={1}>{getDisplayValue(voltage?.toFixed(1), 'v')}</Typography>
            </TableCell>
            <TableCell>
                <Typography numberOfLines={1}>{getDisplayValue(cca, ' Amps')}</Typography>
            </TableCell>
            <TableCell>
                <Typography numberOfLines={1}>{getDisplayValue(toolType)}</Typography>
            </TableCell>
            <TableCell>
                <Typography numberOfLines={1}>#{getDisplayValue(locationId)}</Typography>
            </TableCell>
        </TableRow>
    );
};

export const BmisList: FC = () => {
    const [bmisTableRows, setBmisTableRows] = useState<BmisTableRowModel[]>([]);
    const classes = useBmisListStyles();

    const bmisDispatch = useBmisDispatch();
    const { bmisData, loading: isLoading } = useBmisState();
    const { t } = useTranslation();
    const { vin: vinString } = useParams<{ vin: string }>();

    useEffect(() => {
        setVin(bmisDispatch, bmisService, { vin: vinString });
    }, [bmisDispatch, vinString]);

    useEffect(() => {
        if (bmisData) {
            setBmisTableRows(
                bmisData.map((item) => ({
                    ...item,
                    rowId: item.rowGuid,
                    checked: false,
                }))
            );
        }
    }, [bmisData]);

    const renderHeader = useCallback(() => <BmisTableHeader />, []);
    const renderRow = useCallback((rowData: BmisTableRowModel) => <BmisTableRow rowData={rowData} />, []);

    return (
        <BmisLayout>
            <div className={`pt-7 pl-7 pr-7 d-flex ${!bmisData?.length && classes.tableContainer}`}>
                <div className="d-flex w-100 flex-column">
                    <header className="mb-6 d-flex align-items-center">
                        <Typography variant="h2">{t('bmis:title', BMIS_TITLE)}</Typography>
                        <OverlayTrigger
                            key={'info'}
                            placement="bottom"
                            overlay={
                                <Tooltip className={classes.tooltip} id={'info-tool-tip'}>
                                    {t('bmis:tooltipMessage', BMIS_TOOLTIP)}
                                </Tooltip>
                            }
                        >
                            <Icon name="info" style={{ marginLeft: 15 }} color="black" />
                        </OverlayTrigger>
                    </header>

                    <TableRenderer<BmisTableRowModel>
                        emptyStateProps={{ description: t('bmis:bmisStateDesc', EMPTY_STATE_DESC) }}
                        isLoading={isLoading}
                        outerTableClassName={classes.outerTable}
                        tableBodyRowRenderer={renderRow}
                        tableHeaderRowRenderer={renderHeader}
                        tableRowsData={bmisTableRows}
                        tableRowsDataSetter={setBmisTableRows}
                    />
                </div>
            </div>
        </BmisLayout>
    );
};
